import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, Download, Share2 } from "lucide-react";
import HelpService, { HelpItem } from "../../../Axios/services/help.service";
import { Streamer } from "../../core/Streamer/Streamer";
import MediaContainer from "../FullContentPage/MediaContainer";
import { openLink, sendFileAsAttachment } from "../../content/Globals/globals";
import { extractFileName } from "../Help/utils/helpUtils";
import styles from "./HelpItemPage.module.scss";

const HelpItemPage: React.FC = () => {
  const navigate = useNavigate();
  // @ts-ignore - TypeScript version issue with useParams
  const params = useParams();
  const id = params.id;
  const [helpItem, setHelpItem] = useState<HelpItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attachmentSent, setAttachmentSent] = useState(false);
  const { me } = useSelector((state: any) => state.auth);

  const getHelpItem = useCallback(async () => {
    if (!id) {
      setError("No help item ID provided");
      setLoading(false);
      return;
    }
    
    // Wait for authentication state to be determined
    if (!me) {
      return; // Don't make API call yet, wait for user data
    }
    
    try {
      setLoading(true);
      setError(null);
      const item = await HelpService.getById(id);
      setHelpItem(item);
    } catch (error: any) {
      console.error("Failed to fetch help item:", error);
      
      // Check if it's an authentication error
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        setError("Authentication required. Please log in to view this content.");
      } else if (error?.response?.status === 404) {
        setError("Help item not found. It may have been removed or the link is incorrect.");
      } else {
        setError("Failed to load help item. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  }, [id, me]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    
    // Only call getHelpItem when we have user data or when user data changes
    if (me) {
      getHelpItem();
    }
  }, [getHelpItem, me]);

  const handleDownload = (e: any) => {
    if (helpItem?.URL__c) {
      openLink(helpItem.URL__c, e);
    }
  };

  const handleShare = (e: any) => {
    if (helpItem?.URL__c && me) {
      setAttachmentSent(true);
      sendFileAsAttachment(helpItem.URL__c, e, me, setAttachmentSent);
    }
  };

  const goBack = () => {
    navigate("/help");
  };

  const renderMedia = () => {
    if (!helpItem?.URL__c) return null;

    if (helpItem.Type__c === "Video") {
      return (
        <div className={styles.videoContainer}>
          <Streamer file={helpItem.URL__c} />
        </div>
      );
    }

    if (helpItem.Type__c === "PDF") {
      return (
        <MediaContainer
          link={helpItem.URL__c}
          onDownload={handleDownload}
          onShare={handleShare}
          isPdf={true}
        />
      );
    }

    return null;
  };

  // Show loading while waiting for authentication or while fetching data
  if (loading || !me) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner}>
            <div className={styles.chase}>
              <div className={styles.chaseDot}></div>
              <div className={styles.chaseDot}></div>
              <div className={styles.chaseDot}></div>
              <div className={styles.chaseDot}></div>
              <div className={styles.chaseDot}></div>
              <div className={styles.chaseDot}></div>
            </div>
          </div>
          <p className={styles.loadingText}>
            {!me ? "Authenticating..." : "Loading help item..."}
          </p>
        </div>
      </div>
    );
  }

  if (!helpItem && !loading && error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <h2>Unable to Load Help Item</h2>
          <p>{error}</p>
          <div className={styles.errorActions}>
            <button className={styles.backButton} onClick={goBack}>
              <ArrowLeft size={20} />
              Back to Help
            </button>
            <button 
              className={styles.retryButton} 
              onClick={() => {
                if (me) {
                  setError(null);
                  getHelpItem();
                }
              }}
              disabled={!me}
            >
              {!me ? "Please wait..." : "Try Again"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.helpItemPage}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <button className={styles.backButton} onClick={goBack}>
            <ArrowLeft size={20} />
            <span>Back to Help</span>
          </button>
          
          <div className={styles.headerActions}>
            {helpItem.URL__c && (
              <>
                <button 
                  className={styles.actionButton}
                  onClick={handleDownload}
                  title="Download file"
                >
                  <Download size={20} />
                </button>
                {me && (
                  <button 
                    className={styles.actionButton}
                    onClick={handleShare}
                    title="Share file"
                  >
                    <Share2 size={20} />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        <div className={styles.contentHeader}>
          <div className={styles.typeLabel}>
            {helpItem.Type__c}
          </div>
          <h1 className={styles.title}>{helpItem.Title__c}</h1>
          {helpItem.Description__c && (
            <p className={styles.description}>{helpItem.Description__c}</p>
          )}
          {helpItem.URL__c && (
            <div className={styles.fileInfo}>
              <span className={styles.fileName}>
                {extractFileName(helpItem.URL__c)}
              </span>
            </div>
          )}
        </div>

        {/* Media Display */}
        <div className={styles.mediaSection}>
          {renderMedia()}
        </div>

        {/* Share notification */}
        {attachmentSent && (
          <div className={styles.shareNotification}>
            File shared successfully!
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpItemPage;